import { cva } from 'class-variance-authority';
import { clsx } from 'clsx';
import { type ComponentPropsWithRef, type ReactNode } from 'react';

import { type AllowedDisplayElements } from '@repo/ui/utils/html';
import { getPolymorphicProps } from '@repo/ui/utils/polymorphic';
import {
  type MarginUtilityClass,
  type PaddingUtilityClass,
} from '@repo/ui/utils/spacing';

import s from './Box.module.css';

const DEFAULT_ELEMENT = 'div' as const;

export const boxDisplayPropertyOptions = [
  'block',
  'inline',
  'inline-block',
] as const;
export type BoxDisplayProperties = (typeof boxDisplayPropertyOptions)[number];

/**
 * Props specific to the Box component
 */
type BoxOwnProps<T extends AllowedDisplayElements = typeof DEFAULT_ELEMENT> = {
  as?: T;
  children: ReactNode;
  isContainer?: boolean;
  display?: BoxDisplayProperties;
  isFlex?: boolean;
  margin?: MarginUtilityClass | MarginUtilityClass[];
  padding?: PaddingUtilityClass | PaddingUtilityClass[];
};

/**
 * Full Box props combining own props with HTML element props (including ref)
 */
export type BoxProps<
  T extends AllowedDisplayElements = typeof DEFAULT_ELEMENT,
> = BoxOwnProps<T> & Omit<ComponentPropsWithRef<T>, keyof BoxOwnProps<T>>;

export const boxVariants = cva(s.box, {
  variants: {
    display: {
      block: s.displayBlock,
      inline: s.displayInline,
      ['inline-block']: s.displayInlineBlock,
    },
    isContainer: {
      true: s.isContainer,
    },
    isFlex: {
      true: s.isFlex,
    },
    // the margin and padding variants are not implemented as boxVariants, as they use utility classes and not CSS modules.
  },
  defaultVariants: {},
});

export const Box = <T extends AllowedDisplayElements = typeof DEFAULT_ELEMENT>(
  props: BoxProps<T>,
) => {
  const {
    as = DEFAULT_ELEMENT,
    children,
    className,
    isContainer = false,
    display = 'block',
    isFlex = false,
    margin,
    padding,
    ref,
    ...attrs
  } = props;

  if (!children) {
    return null;
  }

  const { Component, props: componentProps } = getPolymorphicProps(as, {
    ref,
    className: clsx(
      boxVariants({
        className,
        isContainer,
        display,
        isFlex,
      }),
      margin,
      padding,
    ),
    ...attrs,
    children,
  });

  return <Component {...componentProps} />;
};
